import {
  AfterViewChecked,
  Component,
  OnInit,
  ElementRef,
  Injector,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { DrawerSelectEvent } from '@progress/kendo-angular-layout';
import { UserProfile } from '../../auth/profile/profile.service';
import { MenuService } from '../../auth/menu/menu.service';
import { SettingsService } from '../../core/settings/settings.service';
import { MenuItem } from '../../auth/menu-items';
import {
  SidebarDrawerItem,
  selectedItemId,
  toDrawerItems,
} from './sidebar-drawer.model';

declare var $: any;

/** How long the rail flyout survives the pointer leaving a row, so it can be moved into. */
const FLYOUT_CLOSE_DELAY_MS = 150;

/** Layout settings that decide whether the sidebar renders as an icons-only rail. */
const RAIL_SETTINGS = ['isCollapsed', 'isCollapsedText', 'asideHover'];

/**
 * The Portal sidebar (CHAT-857).
 *
 * The renderer is UCP's: a Kendo Drawer over the flat `SidebarDrawerItem[]` projection,
 * replacing the Angle nested-`<ul>` template and the jQuery height-animation / cloned
 * floating-nav that drove it.
 *
 * The MENU is untouched by this component - it renders exactly what MenuService.getMenu()
 * hands back, already filtered by privilege, tier and state (CHAT-855). Nothing here may
 * add, drop or reorder a row.
 *
 * Behaviour kept from the Angle sidebar: one group open at a time, the active route
 * auto-expands its group, and the collapsed rail shows icons with a hover flyout of the
 * hovered group's children.
 */
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit, AfterViewChecked, OnDestroy {
  menuItems: MenuItem[] = [];
  drawerItems: SidebarDrawerItem[] = [];

  /** At most one group open at a time, matching the previous behaviour. */
  expandedGroupId: string | null = null;

  /**
   * The group that owns the current page, whether or not it is open. Distinct from
   * `expandedGroupId`, which a manual click can move: this one follows the ROUTE only, so
   * the parent keeps its emphasis when collapsed or when the user peeks into another group.
   */
  activeGroupId: string | null = null;

  /** The group whose children the rail flyout is showing, if any. */
  flyoutGroup: SidebarDrawerItem | null = null;
  flyoutAnchor: HTMLElement | null = null;

  router: Router;
  sbclickEvent = 'click.sidebar-toggle';
  $doc: any = null;

  profile: UserProfile;

  private destroy$ = new Subject<void>();
  private flyoutCloseTimer: any = null;

  constructor(
    public menu: MenuService,
    public settings: SettingsService,
    public injector: Injector,
    public initialRouter: Router,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>
  ) {
    this.setSidebarWidthCSSVar(this.settings.getLayoutSetting('isCollapsed'));
  }

  ngOnInit(): void {
    this.router = this.injector.get(Router);

    this.settings.layoutSettingsChange
      ?.pipe(takeUntil(this.destroy$))
      .subscribe((change: any) => {
        if (!change) {
          return;
        }
        if (change.key === 'isCollapsed') {
          this.setSidebarWidthCSSVar(change.value);
        }
        // All three decide whether the sidebar is a rail, and the rail renders a different
        // view (no inline children), so any of them has to re-run the Drawer's view build.
        if (RAIL_SETTINGS.indexOf(change.key) > -1) {
          // Leaving or entering the rail drops any flyout that belonged to it.
          this.closeFlyout();
          this.refreshDrawerView();
        }
      });

    // One menu for every scope (CHAT-855) - no bot-count / URL branching, so nothing swaps
    // the menu out from under the user. Seed it now, then re-read on each navigation so a
    // later MenuService.refreshTier() (e.g. after an organization/tier change) is picked up.
    this.updateMenuItems();
    this.syncToRoute();

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        window.scrollTo(0, 0);
        this.settings.setLayoutSetting('asideToggled', false);
        this.closeFlyout();
        this.updateMenuItems();
        this.syncToRoute();
        this.cdr.markForCheck();
      });

    this.anyClickClose();
  }

  ngAfterViewChecked(): void {
    this.syncGroupAriaExpanded();
  }

  load(profile: UserProfile): void {
    this.profile = profile;
  }

  /**
   * Announce whether a group row is open (CHAT-911).
   *
   * The row's expanded state was conveyed by the arrow glyph alone — an
   * `icon-arrow-up` / `icon-arrow-down` class swap on a decorative `<em>`, which
   * carries nothing to a screen reader. `aria-expanded` is what does, and it has
   * to sit on the element that takes focus and holds `role="menuitem"`: the
   * Drawer's own `<li>`. That element is rendered by Kendo, not by our item
   * template, and the Drawer exposes no per-item attribute hook — hence writing
   * it from here.
   *
   * Only groups get the attribute; a leaf row that never expands must not claim
   * to be collapsed, so any stale value is removed rather than set to "false".
   *
   * Runs from ngAfterViewChecked because the state changes without the component
   * being the thing that re-renders (route sync, rail toggle, the Drawer
   * rebuilding its view). Every write is guarded on the value actually differing,
   * so a checked pass that changes nothing touches no DOM.
   */
  private syncGroupAriaExpanded(): void {
    const rows = this.host.nativeElement.querySelectorAll<HTMLElement>('.k-drawer-item');

    rows.forEach((row) => {
      const group = row.querySelector<HTMLElement>('[data-sidebar-group]');

      if (!group) {
        if (row.hasAttribute('aria-expanded')) {
          row.removeAttribute('aria-expanded');
        }
        return;
      }

      const expanded = String(
        !this.isRailMode() && group.getAttribute('data-sidebar-group') === this.expandedGroupId
      );

      if (row.getAttribute('aria-expanded') !== expanded) {
        row.setAttribute('aria-expanded', expanded);
      }
    });
  }

  // -- Drawer state ---------------------------------------------------------------------

  /**
   * Passed to the Drawer as `[isItemExpanded]`, which calls it on each PARENT row to decide
   * whether to emit that row's children. Must stay an arrow property: the Drawer's setter
   * rejects a non-function, and this needs `this`.
   *
   * Nothing expands while the sidebar is a rail - there is no room for inline children, and
   * the flyout is what shows them there instead.
   */
  readonly isItemExpanded = (item: SidebarDrawerItem): boolean =>
    !this.isRailMode() && item.id === this.expandedGroupId;

  get isDrawerExpanded(): boolean {
    return !this.isRailMode();
  }

  /**
   * Icons-only rail. `isCollapsedText` and `asideHover` are folded in because the Angle
   * sidebar treated all three the same way for the purposes of the floating submenu.
   */
  isRailMode(): boolean {
    return RAIL_SETTINGS.some((key) => !!this.settings.getLayoutSetting(key));
  }

  onSelect(event: DrawerSelectEvent): void {
    // We own both selection and expansion - selection follows the router, not the click.
    event.preventDefault();

    const item = event.item as SidebarDrawerItem;
    if (!item) {
      return;
    }

    if (item.isGroup) {
      if (this.isRailMode()) {
        // In the rail a group has nowhere to expand to; surface its flyout instead, so a
        // touch user can reach the children that hover would have shown.
        this.openFlyout(item, this.rowElement(event.originalEvent));
        return;
      }
      this.expandedGroupId = this.expandedGroupId === item.id ? null : item.id;
      this.refreshDrawerView();
      return;
    }

    // Leaves carry a real anchor in the template, so the routerLink on it has already done
    // the navigating by the time this fires. Nothing to do but tidy up.
    this.closeFlyout();
  }

  trackByDrawerItem(index: number, item: SidebarDrawerItem): string {
    return item.id;
  }

  /**
   * The Drawer rebuilds its view only when the `items` INPUT REFERENCE changes - see
   * DrawerComponent.ngOnChanges, which is the sole caller of DrawerService.init(). So a
   * state change that alters what is visible without touching the menu (expanding a group,
   * entering or leaving the rail) still has to hand it a fresh array.
   */
  private refreshDrawerView(): void {
    this.drawerItems = [...this.drawerItems];
    this.cdr.markForCheck();
  }

  // -- Rail flyout ----------------------------------------------------------------------

  onRowHover(event: MouseEvent, item: SidebarDrawerItem): void {
    if (!this.isRailMode()) {
      return;
    }

    this.cancelFlyoutClose();

    if (!item.children || item.children.length === 0) {
      this.closeFlyout();
      this.cdr.markForCheck();
      return;
    }

    this.openFlyout(item, this.rowElement(event));
  }

  onFlyoutEnter(): void {
    this.cancelFlyoutClose();
  }

  /**
   * Deferred so the pointer can cross the gap between the rail row and the flyout without
   * the flyout vanishing underneath it.
   */
  scheduleFlyoutClose(): void {
    this.cancelFlyoutClose();
    this.flyoutCloseTimer = setTimeout(() => {
      this.closeFlyout();
      this.cdr.markForCheck();
    }, FLYOUT_CLOSE_DELAY_MS);
  }

  closeFlyout(): void {
    this.cancelFlyoutClose();
    this.flyoutGroup = null;
    this.flyoutAnchor = null;
  }

  private openFlyout(item: SidebarDrawerItem, anchor: HTMLElement | null): void {
    this.flyoutGroup = item;
    this.flyoutAnchor = anchor;
    this.cdr.markForCheck();
  }

  private cancelFlyoutClose(): void {
    if (this.flyoutCloseTimer) {
      clearTimeout(this.flyoutCloseTimer);
      this.flyoutCloseTimer = null;
    }
  }

  /**
   * The Drawer's `li`, so the flyout anchors to the whole row rather than to the icon.
   *
   * Both candidates are tried because the two callers differ: a hover hands us the row
   * element itself, while a keyboard Enter arrives with `currentTarget` set to the list.
   */
  private rowElement(event: any): HTMLElement | null {
    const candidates = [event?.currentTarget, event?.target];

    for (const candidate of candidates) {
      if (candidate && typeof candidate.closest === 'function') {
        const row = candidate.closest('.k-drawer-item');
        if (row) {
          return row;
        }
      }
    }

    return candidates.find((c) => !!c) ?? null;
  }

  // -- Menu / route sync ----------------------------------------------------------------

  private updateMenuItems(): void {
    const newMenuItems = this.menu.getMenu();

    // Only update if the menu items actually changed to prevent unnecessary re-renders
    if (!this.menuItems || this.menuItemsChanged(this.menuItems, newMenuItems)) {
      this.menuItems = newMenuItems;
      this.drawerItems = toDrawerItems(newMenuItems);
      this.cdr.markForCheck(); // Only trigger change detection when menu actually changes
    }
  }

  /**
   * Points selection at the row that owns the current URL, and DERIVES the open group from
   * it: the selected child's parent, or nothing when the selected row is top-level.
   *
   * That second half is the auto-collapse rule. Expansion is not sticky state that
   * accumulates - each navigation recomputes it from scratch, so moving to a top-level page
   * closes whatever was open and moving into a different group closes the previous one.
   * Only the group holding the current page stays open. This is what UCP's own sidebar does
   * (`updateSelectedItem` -> `expandedIndices = selectedParentId !== null ? [id] : []`).
   *
   * A manual click on a group parent still toggles it - see onSelect. That is deliberate:
   * it lets a user peek into another group without navigating, and it survives until the
   * next navigation recomputes from the route.
   */
  private syncToRoute(): void {
    const url = this.router?.url ?? '';
    const selectedId = selectedItemId(this.drawerItems, url);

    this.drawerItems = this.drawerItems.map((item) => ({
      ...item,
      selected: item.id === selectedId,
      children: item.children?.map((child) => ({
        ...child,
        selected: child.id === selectedId,
      })),
    }));

    const selected = this.drawerItems.find((item) => item.id === selectedId);
    this.expandedGroupId = selected?.parentId ?? null;
    this.activeGroupId = selected?.parentId ?? null;
  }

  private menuItemsChanged(
    oldItems: MenuItem[],
    newItems: MenuItem[]
  ): boolean {
    if (!oldItems && !newItems) return false;
    if (!oldItems || !newItems) return true;
    if (oldItems.length !== newItems.length) return true;

    // Compare key properties to detect meaningful changes, including inside groups - a
    // tier refresh can change a group's children without changing the parent row.
    for (let i = 0; i < oldItems.length; i++) {
      if (
        oldItems[i].text !== newItems[i].text ||
        oldItems[i].link !== newItems[i].link ||
        oldItems[i].icon !== newItems[i].icon
      ) {
        return true;
      }
      if (
        this.menuItemsChanged(
          oldItems[i].submenu ?? [],
          newItems[i].submenu ?? []
        )
      ) {
        return true;
      }
    }
    return false;
  }

  // -- Shell ----------------------------------------------------------------------------

  private anyClickClose(): void {
    this.$doc = $(document).on(this.sbclickEvent, (e) => {
      if (!$(e.target).parents('.aside-container').length) {
        this.settings.setLayoutSetting('asideToggled', false);
      }
    });
  }

  ngOnDestroy(): void {
    this.cancelFlyoutClose();
    this.destroy$.next();
    this.destroy$.complete();
    if (this.$doc) {
      this.$doc.off(this.sbclickEvent);
    }
  }

  private setSidebarWidthCSSVar(isCollapsed: boolean): void {
    const width = isCollapsed ? '60px' : '220px';
    document.documentElement.style.setProperty('--sidebar-width', width);
  }

  toggleCollapsedSideabar(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.closeFlyout();
    this.settings.toggleLayoutSetting('isCollapsed');
  }
}
